import { z } from 'zod';
import { qaCategorySchema, secondModelFailureSchema, severitySchema, UNRESOLVED, } from './generation.js';
/**
 * The lesson QA scorecard — six category scores, one blended score, one status.
 *
 * Derived from the findings the reviewer already writes rather than from a second pass of
 * its own. `ClaudeReviewer` is independent by construction — spec 013 §8.2 gives it a
 * fresh `ask` so a model never grades the conversation it wrote in — which is the
 * property a scorecard needs, and the reason this is arithmetic rather than another agent.
 *
 * Pure and shared: the API serves it and the console renders it, and a second copy of the
 * weights is how a screen disagrees with the number it is showing.
 */
/**
 * What one unresolved finding costs the category it names.
 *
 * Severity and not a per-finding number, because a reviewer returns severities and
 * nothing else — `findingSchema` has no field a score could be read from, and adding one
 * would ask a model to grade itself in the same breath it reports.
 */
const COST = { BLOCKER: 40, WARN: 15, NOTE: 5 };
const COUNTED_AS = {
    BLOCKER: 'blockers',
    WARN: 'warnings',
    NOTE: 'notes',
};
const hundredths = (value) => Math.round(value * 100) / 100;
/**
 * How much of the blended score each category carries. They sum to 1.
 *
 * Grounding is weighted highest because it is the one failure a reader cannot detect:
 * `content-grounding` calls it the product's differentiator, and an invented statistic
 * survives review precisely when it is well-formed.
 *
 * Comprehension is second, level with mechanics: a screen the learner cannot follow and
 * one graded wrong leave them equally unable to act. Writing pays most of it, since its
 * two mechanical rules are publish-gate blockers already and were being counted twice.
 * Where the other 0.10 came from: `docs/decisions/comprehension-category.md`.
 */
export const QA_WEIGHTS = {
    GROUNDING: 0.25,
    COMPREHENSION: 0.2,
    MECHANICS: 0.2,
    INSTRUCTIONAL_DESIGN: 0.15,
    WRITING: 0.1,
    RELEVANCE: 0.1,
};
/**
 * The order a panel draws them in, which is the order they are weighted.
 *
 * Derived rather than restated. Every other place the six are enumerated is checked by
 * something: the records are `Record<QaCategory, …>`, `scores` must satisfy its schema.
 * The console's own array was the one restatement no gate covered, so a seventh category
 * would have shipped invisible with everything green.
 */
export const QA_CATEGORY_ORDER = Object.keys(QA_WEIGHTS);
/** What each category is called on screen. One copy, so two panels cannot disagree. */
export const QA_CATEGORY_LABEL = {
    GROUNDING: 'Grounding & integrity',
    COMPREHENSION: 'Clear to a first-time learner',
    INSTRUCTIONAL_DESIGN: 'Instructional design',
    MECHANICS: 'Learning mechanics',
    WRITING: 'Writing & copy',
    RELEVANCE: 'Practical relevance',
};
/** At or above `QA_PASS_AT` a lesson passes; below `QA_REVIEW_AT` it fails outright. */
export const QA_PASS_AT = 90;
export const QA_REVIEW_AT = 80;
/**
 * The `lesson-design` rules a second model answers on every reviewed lesson — GEN-100.
 *
 * Each rule's share of the rubric score, and the category its failing answer is filed
 * under. The weights sum to 1. The questions themselves are the generator's; the numbers
 * are here because the scorecard reads them.
 */
export const RUBRIC = {
    DO_FIRST: { category: 'INSTRUCTIONAL_DESIGN', weight: 0.2 },
    NOT_A_QUIZ: { category: 'INSTRUCTIONAL_DESIGN', weight: 0.15 },
    CLIMBS_THE_LADDER: { category: 'MECHANICS', weight: 0.15 },
    ENDS_HARDER: { category: 'INSTRUCTIONAL_DESIGN', weight: 0.15 },
    TRANSFER: { category: 'RELEVANCE', weight: 0.15 },
    DECISION_NOT_DEFINITION: { category: 'RELEVANCE', weight: 0.1 },
    BUILDS_ON: { category: 'INSTRUCTIONAL_DESIGN', weight: 0.1 },
};
/**
 * Below this the lesson wants a look. Never a failure: the rubric is on trial until there
 * is data to say what its numbers mean, so it can send a lesson to a person and no further.
 */
export const RUBRIC_PASS_AT = 80;
const outOf100 = z.number().int().min(0).max(100);
export const qaStatusSchema = z
    .enum(['PASSED', 'NEEDS_REVIEW', 'FAILED'])
    .describe('Whether the lesson is ready, wants a look, or wants work.');
const count = z.number().int().nonnegative();
/**
 * One rule the verdict tripped — GEN-174. The first three fail a lesson, the rest send it
 * for review. Flat rather than a union, because the SDK drops a union's discriminator.
 */
export const qaReasonSchema = z.strictObject({
    rule: z.enum([
        'OPEN_BLOCKERS',
        'CATEGORY_BELOW',
        'BLEND_BELOW',
        'RUBRIC_BELOW',
        'RUBRIC_UNSCORED',
        'UNCITED',
    ]),
    category: qaCategorySchema.optional(),
    value: z
        .number()
        .int()
        .optional()
        .describe('The blocker count, or the score that fell short.'),
    line: outOf100.optional().describe('The line the score fell short of.'),
});
/**
 * Every number the scorecard was worked out from — GEN-174. Served so the console can show
 * the arithmetic without holding a second copy of the formula.
 */
export const qaBreakdownSchema = z.strictObject({
    costs: z.strictObject({ BLOCKER: count, WARN: count, NOTE: count }),
    categories: z.array(z.strictObject({
        category: qaCategorySchema,
        blockers: count,
        warnings: count,
        notes: count,
        lost: count.describe('What its open findings cost, before the floor at 0.'),
        score: outOf100,
        weight: z.number().min(0).max(1),
    })),
    blend: z
        .number()
        .describe('The sum of score × weight, before rounding to overall.'),
    passAt: outOf100,
    reviewAt: outOf100,
    rubricPassAt: outOf100,
    reasons: z
        .array(qaReasonSchema)
        .describe('The rules that decided status: fail rules when FAILED, review rules when NEEDS_REVIEW, none when PASSED.'),
});
export const qaScorecardSchema = z
    .strictObject({
    /**
     * Each category out of 100, named one at a time.
     *
     * Not `z.record(qaCategorySchema, …)`, for the reason `lessonWorkspaceSchema.usage`
     * is a list: a record has no shape in JSON Schema, so the SDK types it as an index
     * signature and the console loses the six names it renders. A seventh category is a
     * migration and a weight; adding it here too is the cheap half.
     */
    scores: z.strictObject({
        GROUNDING: outOf100,
        COMPREHENSION: outOf100,
        INSTRUCTIONAL_DESIGN: outOf100,
        MECHANICS: outOf100,
        WRITING: outOf100,
        RELEVANCE: outOf100,
    }),
    overall: outOf100.describe('The weighted blend of the six.'),
    status: qaStatusSchema,
    blockers: z
        .number()
        .int()
        .nonnegative()
        .describe('Unresolved BLOCKER findings. One of them fails the lesson whatever the blend says.'),
    uncategorised: z
        .number()
        .int()
        .nonnegative()
        .describe('Unresolved findings naming no category, so no score moved for them.'),
    rubric: outOf100
        .nullable()
        .optional()
        .describe("The second model's lesson-design rubric. Null when it was asked and could not answer; absent when it was never asked."),
    rubricFailure: secondModelFailureSchema
        .optional()
        .describe('Why the rubric is null; absent when it was scored or never asked.'),
    claimsFailure: secondModelFailureSchema
        .optional()
        .describe("Why the lesson's claims were not checked against its source; absent when they were."),
    cited: z
        .boolean()
        .optional()
        .describe('Whether the lesson cites any source. False means grounding was not checked against one, and the lesson cannot pass; absent when it was never asked.'),
    breakdown: qaBreakdownSchema,
})
    .describe('What the reviewers think of a lesson, in eight numbers.');
/**
 * A finding still standing against the lesson — `UNRESOLVED`, not a second copy of it.
 *
 * `ACCEPTED` and `DISMISSED` are both decisions, and a decided finding is not a defect:
 * one was fixed and the other was overruled. `PROPOSED` is neither — GEN-76 — so it still
 * counts, on the same argument that keeps it out of the publish gate. That argument is
 * made once, where the publish gate reads it.
 */
const standing = (finding) => UNRESOLVED.includes(finding.status);
/**
 * The six scores, the blend, and the verdict — GEN-89, GEN-93.
 *
 * The verdict is not `overall` thresholded. A blend hides its worst input by design, and
 * the point of scoring six things separately is that a lesson with excellent writing and
 * invented sources must not average its way to PASSED. So a category below the review
 * line fails the lesson on its own, and so does any unresolved blocker — including one
 * carrying no category, because the publish gate's own refusals arrive that way and a
 * lesson that cannot publish has not passed QA. That floor is why GEN-93 is a category
 * and not a stricter prompt: a screen nobody can follow now sinks one of the six.
 */
export function qaScorecard(findings, rubric, cited) {
    const tally = Object.fromEntries(QA_CATEGORY_ORDER.map((category) => [
        category,
        { blockers: 0, warnings: 0, notes: 0, lost: 0 },
    ]));
    let blockers = 0;
    let uncategorised = 0;
    // One pass. The list is every finding ever raised against the lesson and grows with each
    // job that reviews it, and this runs on every read of the console's workspace.
    for (const finding of findings) {
        if (!standing(finding))
            continue;
        if (finding.severity === 'BLOCKER')
            blockers += 1;
        if (finding.category === undefined) {
            uncategorised += 1;
            continue;
        }
        const row = tally[finding.category];
        row.lost += COST[finding.severity];
        row[COUNTED_AS[finding.severity]] += 1;
    }
    const categories = QA_CATEGORY_ORDER.map((category) => {
        const score = 100 - Math.min(100, tally[category].lost);
        const weight = QA_WEIGHTS[category];
        return {
            category,
            ...tally[category],
            score,
            weight,
        };
    });
    const scores = Object.fromEntries(categories.map((row) => [row.category, row.score]));
    // To two places, so a blend of 89.5 does not read 89.4999… and round the wrong way.
    const blend = hundredths(categories.reduce((sum, row) => sum + row.score * row.weight, 0));
    const overall = Math.round(blend);
    const below = (line) => [
        ...categories
            .filter((row) => row.score < line)
            .map((row) => ({
            rule: 'CATEGORY_BELOW',
            category: row.category,
            value: row.score,
            line,
        })),
        ...(overall < line
            ? [{ rule: 'BLEND_BELOW', value: overall, line }]
            : []),
    ];
    const failing = [
        ...(blockers > 0
            ? [{ rule: 'OPEN_BLOCKERS', value: blockers }]
            : []),
        ...below(QA_REVIEW_AT),
    ];
    // An unsourced lesson wants a look, never a failure: the publish gate asks a source
    // of each asserting scene, not of the lesson, and QA is not stricter than the gate.
    const reviewing = [
        ...below(QA_PASS_AT),
        ...(rubric === null ? [{ rule: 'RUBRIC_UNSCORED' }] : []),
        ...(typeof rubric === 'number' && rubric < RUBRIC_PASS_AT
            ? [{ rule: 'RUBRIC_BELOW', value: rubric, line: RUBRIC_PASS_AT }]
            : []),
        ...(cited === false ? [{ rule: 'UNCITED' }] : []),
    ];
    const reasons = failing.length > 0 ? failing : reviewing;
    return {
        scores,
        overall,
        blockers,
        uncategorised,
        ...(rubric === undefined ? {} : { rubric }),
        ...(cited === undefined ? {} : { cited }),
        status: failing.length > 0
            ? 'FAILED'
            : reviewing.length > 0
                ? 'NEEDS_REVIEW'
                : 'PASSED',
        breakdown: {
            costs: COST,
            categories,
            blend,
            passAt: QA_PASS_AT,
            reviewAt: QA_REVIEW_AT,
            rubricPassAt: RUBRIC_PASS_AT,
            reasons,
        },
    };
}
/**
 * Published lessons with scenes QA says a first-time learner cannot act on — per-scene QA.
 *
 * Filled by the one-time `qa:audit` run over lessons published before scenes were checked
 * one at a time. Listed, never unpublished: a person decides which to fix first.
 */
export const qaAuditSchema = z
    .strictObject({
    lessons: z.array(z.strictObject({
        id: z.string(),
        slug: z.string(),
        title: z.string(),
        scenes: z.array(z.strictObject({
            sceneId: z.string(),
            position: z.number().int(),
            prompt: z.string(),
            findings: z.array(z.strictObject({
                id: z.string(),
                severity: severitySchema.extract(['BLOCKER', 'WARN']),
                finding: z.string(),
                suggestion: z.string(),
            })),
        })),
    })),
})
    .describe('Failing scenes of published lessons, grouped by lesson, blockers first.');
//# sourceMappingURL=qa-score.js.map